import type React from "react";
import { useState, useEffect, useRef } from "react";

interface LlmProviderDropdownProps {
  defaultValue?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const providers = [
  { value: "openai", label: "OpenAI" },
  { value: "mistral", label: "Mistral" },
];

const LlmProviderDropdown: React.FC<LlmProviderDropdownProps> = ({
  defaultValue = "openai",
  onChange,
  disabled = false,
}) => {
  const [selected, setSelected] = useState<string>(defaultValue);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) {
      setSelected(defaultValue);
    }
  }, [defaultValue, disabled]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (value: string) => {
    setSelected(value);
    setOpen(false);
    onChange(value);
  };

  const displayText = providers.find((p) => p.value === selected)?.label ?? selected;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
          disabled
            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
            : "bg-white text-gray-600 border-gray-300 hover:border-gray-400 hover:bg-gray-50 cursor-pointer"
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
        {displayText}
        {!disabled && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>
      {open && (
        <div className="absolute bottom-full mb-1 left-0 w-28 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
          {providers.map((provider) => (
            <button
              key={provider.value}
              type="button"
              onClick={() => handleSelect(provider.value)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors ${
                selected === provider.value
                  ? "text-green-600 font-semibold bg-green-50"
                  : "text-gray-700"
              }`}
            >
              {provider.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LlmProviderDropdown;
